import { type CallHomeServiceMap } from '@solinkcorp/vd-common';
import StorageAPI, { PERSISTENT_PREFIX } from './StorageAPI';
import { IS_DEV, IS_STAGING } from './environment';

const SETTINGS_OVERRIDE_KEY = PERSISTENT_PREFIX + 'SETTINGS_OVERRIDE';

interface SettingsDefaults {
  _host: string;
}
type SettingsOverrides = Partial<SettingsDefaults>;

const DOMAIN_SOLINK = '.solinkcloud.com';
const PUBLIC_URL = 'https://public.solinkcloud.com';
let AUTH_SOLINK = 'https://auth.solink.direct';
let SIGNALLER_URL = 'https://signaller.solinkcloud.com';
let INTERCOM_ID = 'p9rbi4pn';
let AUTH0_CLIENT_ID = 'plrgnwwL0jIvD4JoCiq5BRVSyolBbHcw';
let AUTH0_DOMAIN = 'solink.auth0.com';
const BRAND_NAME = 'solink';
let CALLHOME_URL = 'https://callhome.solinkcloud.com';
let MANAGE_URL = 'https://manage.solinkcloud.com';
const STATUS_URL = 'https://status.solinkcloud.com/api/v2/status.json';
const STATUS_SUMMARY_URL = 'https://status.solinkcloud.com/api/v2/summary.json';
const STATUS_PAGE_URL = 'https://status.solinkcloud.com/';
const HELP_CENTER = 'https://help.solink.com/';
const HELP_CENTER_WIDGET_CREATION = `${HELP_CENTER}Content/Dashboard%20and%20Widgets/Create%20a%20Widget.htm`;
const HELP_CENTER_THRESHOLD_CREATION = `${HELP_CENTER}Content/Dashboard%20and%20Widgets/Threshold%20Notifications.htm`;
const SOLINK_WORDPRESS_DOMAIN = 'https://solink.com';
const DEVICE_PROXY_URL = 'https://device-proxy-prod.solinkcloud.com';
let PUBLIC_CLOUD_VMS_HOST = 'https://cloudvms.solinkcloud.com';
let MONITOR_CENTER_HOST = 'https://monitor-center-system.solinkcloud.com';
let CLOUD_AI_HOST = 'https://cloud-ai.solinkcloud.com';
let CLOUD_AI_ALB_HOST = 'https://cloud-ai-alb.solinkcloud.com';
const SOLINK_UPGRADE_URL = 'https://solink.com/upgrade/';
let ACCESS_CONTROL_SERVICE = 'https://access-control.solinkcloud.com';
const EVENTS_API_URL = 'https://events.solinkcloud.com';
let VD_API_URL = 'https://callhome.solinkcloud.com/vd';
let ALARM_TESTER_URL = 'https://alarm-tester.solinkcloud.com';
const ALARM_TESTER_URL_EU = 'https://alarm-tester-prod-eu-central-1.solinkcloud.com';
let DTMF_PIN_PHONE_NUMBER = '+1 (888) 789-2576';
// Prod keeps the `prod-` prefix here (confirmed against solink-alarm-dtmf-pin's docs).
let DTMF_PIN_URL = 'https://prod-alarm-dtmf.solinkcloud.com';

if (IS_DEV) {
  AUTH_SOLINK = 'https://auth.dev.solink.direct';
  SIGNALLER_URL = 'https://signaller.dev.solinkcloud.com';
  INTERCOM_ID = 'rm5ya6w8';
  AUTH0_CLIENT_ID = 'HEFiirzqMoilvzvSkwhrvZpD10HlSQU6';
  AUTH0_DOMAIN = 'solink-dev.us.auth0.com';
  CALLHOME_URL = 'https://callhome.dev.solinkcloud.com';
  MANAGE_URL = 'https://manage.dev.solinkcloud.com';
  PUBLIC_CLOUD_VMS_HOST = 'https://cloudvms.dev.solinkcloud.com';
  MONITOR_CENTER_HOST = 'https://monitor-center-system.dev.solinkcloud.com';
  CLOUD_AI_HOST = 'https://cloud-ai.dev.solinkcloud.com';
  CLOUD_AI_ALB_HOST = 'https://cloud-ai-alb.dev.solinkcloud.com';
  ACCESS_CONTROL_SERVICE = 'https://access-control.dev.solinkcloud.com';
  VD_API_URL = 'https://callhome.dev.solinkcloud.com/vd';
  ALARM_TESTER_URL = 'https://alarm-tester.dev.solinkcloud.com';
  DTMF_PIN_PHONE_NUMBER = '+1 (613) 707-7745';
  DTMF_PIN_URL = 'https://alarm-dtmf.dev.solinkcloud.com';
}

if (IS_STAGING) {
  AUTH_SOLINK = 'https://auth.staging.solink.direct';
  SIGNALLER_URL = 'https://signaller.staging.solinkcloud.com';
  INTERCOM_ID = 'rm5ya6w8';
  AUTH0_CLIENT_ID = 'lfvNfl9tDbvMG1sqYFQOqYAh6vJkBMBU';
  AUTH0_DOMAIN = 'solink-staging.us.auth0.com';
  CALLHOME_URL = 'https://callhome.staging.solinkcloud.com';
  MANAGE_URL = 'https://manage.staging.solinkcloud.com';
  PUBLIC_CLOUD_VMS_HOST = 'https://cloudvms.staging.solinkcloud.com';
  MONITOR_CENTER_HOST = 'https://monitor-center-system.staging.solinkcloud.com';
  CLOUD_AI_HOST = 'https://cloud-ai.staging.solinkcloud.com';
  CLOUD_AI_ALB_HOST = 'https://cloud-ai-alb.staging.solinkcloud.com';
  ACCESS_CONTROL_SERVICE = 'https://access-control.staging.solinkcloud.com';
  VD_API_URL = 'https://callhome.staging.solinkcloud.com/vd';
  ALARM_TESTER_URL = 'https://alarm-tester.staging.solinkcloud.com';
  DTMF_PIN_PHONE_NUMBER = '+1 (613) 519-4113';
  DTMF_PIN_URL = 'https://alarm-dtmf.staging.solinkcloud.com';
}

const FusionChartKey =
  'PtB2dF-7wB4C5C3C5E4D3F4C2B10C9D2B3F6snnD3B1D8fC5C5D4H-9B-7iA7A3A3wqg1F2B1H2B-22moD2E6E1D3B2C2C7A4C7A3B4g1jjH3B5QB5jnB-8E3E1G2tgE3HA1E2A-7A-7nB3B7D1D4D3B2H2A18A14A7C9C3E4B4t==';

class Settings {
  public auth_solink = AUTH_SOLINK;
  public auth0_clientID = AUTH0_CLIENT_ID;
  public auth0_domain = AUTH0_DOMAIN;
  public brand = BRAND_NAME;
  public callhome_url = CALLHOME_URL;
  public manage_url = MANAGE_URL;
  public domain_solink = DOMAIN_SOLINK;
  public intercom_id = INTERCOM_ID;
  public signaller_url = SIGNALLER_URL;
  public status_url = STATUS_URL;
  public status_summary_url = STATUS_SUMMARY_URL;
  public status_page_url = STATUS_PAGE_URL;
  public dtmfPinUrl = DTMF_PIN_URL;
  public dtmfPinArmPhoneNumber = DTMF_PIN_PHONE_NUMBER;
  public helpCenter = HELP_CENTER;
  public helpCenterWidgetCreation = HELP_CENTER_WIDGET_CREATION;
  public helpCenterThresholdCreation = HELP_CENTER_THRESHOLD_CREATION;
  public solinkWordpressDomain = SOLINK_WORDPRESS_DOMAIN;
  public device_proxy_url = DEVICE_PROXY_URL;
  public FCKey = FusionChartKey;
  public publicAlarmHost = PUBLIC_CLOUD_VMS_HOST;
  public publicCloudVmsHost = PUBLIC_CLOUD_VMS_HOST;
  public monitorCenterHost = MONITOR_CENTER_HOST;
  public cloudAIHost = CLOUD_AI_HOST;
  public cloudAiAlbHost = CLOUD_AI_ALB_HOST;
  public publicUrl = PUBLIC_URL;
  public solinkUpgradeUrl = SOLINK_UPGRADE_URL;
  public accessControlServiceUrl = ACCESS_CONTROL_SERVICE;
  public events = EVENTS_API_URL;
  public alarmTesterUrl = ALARM_TESTER_URL;
  public alarmTesterUrlEu = ALARM_TESTER_URL_EU;
  public vdApiUrl = VD_API_URL;
  /**
   * The user-region specific URLs or buckets we get from authorize response after user login.
   */

  /** The URL for cloud-vms cloud front. It's where all the video assets are.
   * eg. https://vms.solinkcloud.com
   */
  private _cloud_front_vms_host: string;
  /** The URL for cloud-vms. eg. https://cloudvms.solinkcloud.com */
  private _cloud_vms_host: string;
  /** The URL for platform-api. eg. https://client.solinkcloud.com */
  private _host: string;
  /** The URL for beta platform-api. eg. https://beta-client.solinkcloud.com */
  private _beta_client: string | undefined;
  /** The URL for beta events-api. eg. https://beta-events.solinkcloud.com */
  private _beta_events: string | undefined;
  /** Cloudfront distrubition for images. eg. https://images.solinkcloud.com */
  private _images_url: string;
  /** Images S3 bucket. eg. solinkimages */
  private _s3_bucket_name: string;
  /** AWS region. eg. us-west-2 */
  private _aws_region: string;

  private _defaults: SettingsDefaults;

  private _userServiceMap: CallHomeServiceMap | undefined;
  private _overrides: SettingsOverrides | undefined | null;

  constructor() {
    this._cloud_front_vms_host = '';
    this._cloud_vms_host = '';
    this._host = '';
    this._images_url = '';
    this._s3_bucket_name = '';
    this._aws_region = '';
    this._beta_client = '';

    // These can be override by alternative options, like beta, for internal testing.
    this._defaults = {
      _host: '',
    };

    const overrides = StorageAPI.getLocally<SettingsOverrides>(SETTINGS_OVERRIDE_KEY);
    this._applyOverrides(overrides);
  }

  get cloud_front_vms_host() {
    if (!this._cloud_front_vms_host) {
      throw new Error('cloud_front_vms_host is not set');
    }
    return this._cloud_front_vms_host;
  }

  get cloud_vms_host() {
    if (!this._cloud_vms_host) {
      throw new Error('cloud_vms_host is not set');
    }
    return this._cloud_vms_host;
  }

  get host() {
    if (!this._host) {
      throw new Error('host is not set');
    }
    console.log('Getting host value of: ' + this._host);
    return this._host;
  }

  set host(value) {
    this._host = value;
    console.log('Setting _host value to: ' + value);
  }

  get images_url() {
    if (!this._images_url) {
      throw new Error('images_url is not set');
    }
    return this._images_url;
  }

  get s3_bucket_name() {
    if (!this._s3_bucket_name) {
      throw new Error('s3_bucket_name is not set');
    }
    return this._s3_bucket_name;
  }

  get aws_region() {
    if (!this._aws_region) {
      throw new Error('aws_region is not set');
    }
    return this._aws_region;
  }

  /**
   * Get the AI verification debug endpoint hostname based on stage and AWS region
   * @throws Error if the stage/region combination is not supported
   */
  get aiVerificationDebugHost() {
    const awsRegion = this._aws_region;
    if (!awsRegion) {
      throw new Error('aws_region is not set');
    }

    if (awsRegion === 'eu-central-1') {
      return this.alarmTesterUrlEu;
    }
    return this.alarmTesterUrl;
  }

  get beta_client() {
    return this._beta_client;
  }

  get beta_events() {
    return this._beta_events;
  }

  get isUsingBetaPlatformApi() {
    return this._host === this._beta_client;
  }

  get isUsingBetaEventsApi() {
    return this._host === this._beta_events;
  }

  /**
   * Update settings with values from serviceMap.
   *
   * This is called every time a user logs in.
   */
  update(serviceMap: CallHomeServiceMap) {
    this._userServiceMap = serviceMap;

    this._cloud_front_vms_host = serviceMap.cloudVmsCdnUrl;
    this._cloud_vms_host = serviceMap.cloudVmsApiUrl;
    this._images_url = serviceMap.imagesCdnUrl;
    this._s3_bucket_name = serviceMap.imagesS3Bucket;
    this._aws_region = serviceMap.awsRegion;
    /** If betaClientApiUrl is not returned, user shouldn't access beta client API. EU doesn't have beta environment. */
    this._beta_client = serviceMap.betaClientApiUrl;
    /** If betaEventsApiUrl is not returned, user shouldn't access beta events ingetst API. EU doesn't have beta environment. */
    this._beta_events = serviceMap.betaEventsApiUrl;

    // Fall back to the built-in US default when a region's service map
    // doesn't carry a vd URL (vd-common still types it optional).
    if (import.meta.env.VITE_VD_API_URL) {
      this.vdApiUrl = new URL(import.meta.env.VITE_VD_API_URL).href.replace(/\/$/, '');
    } else {
      this.vdApiUrl = serviceMap.vdApiUrl ?? VD_API_URL;
    }

    // Ignore any settings that have been overridden.
    if (!this._overrides?._host) {
      this._host = serviceMap.clientApiUrl;
      this._defaults._host = serviceMap.clientApiUrl;
    }

    // This conditional is only necessary because at the time of development it hasn't been added to the service map. So it can be removed later.
    if (serviceMap.cloudAiApiUrl) {
      this.cloudAIHost = serviceMap.cloudAiApiUrl;
    }
    // Hacky way to get the cloud-ai url for EU users. This should be removed once the service map is updated.
    else if (serviceMap.cloudVmsApiUrl) {
      this.cloudAIHost = serviceMap.cloudVmsApiUrl.replace('cloudvms', 'cloud-ai');
    }

    this.cloudAiAlbHost = this.cloudAIHost.replace('cloud-ai', 'cloud-ai-alb');

    if (serviceMap.monitorCenterUrl) {
      this.monitorCenterHost = serviceMap.monitorCenterUrl;
    }

    if (serviceMap.alarmConfigDtmfApiUrl) {
      this.dtmfPinUrl = serviceMap.alarmConfigDtmfApiUrl;
    }

    this.events = serviceMap.eventsApiUrl;
  }

  /** Set persistent overrides for settings.  These will perist across logins, until cleared. */
  setOverrides(overrides: SettingsOverrides | undefined) {
    this._applyOverrides(overrides);

    if (!overrides) {
      StorageAPI.removeLocally(SETTINGS_OVERRIDE_KEY);
    } else {
      StorageAPI.storeLocally(SETTINGS_OVERRIDE_KEY, overrides);
    }
  }

  toggleBetaPlatformApi(isOn: boolean) {
    if (isOn) {
      this.setOverrides({ _host: this._beta_client });
    } else {
      this.setOverrides(undefined);
    }
  }

  toggleBetaEventsApi(isOn: boolean) {
    if (isOn) {
      this.setOverrides({ _host: this._beta_events });
    } else {
      this.setOverrides(undefined);
    }
  }

  /** Apply overrides to the settings. */
  private _applyOverrides(overrides: SettingsOverrides | undefined | null) {
    this._overrides = overrides;

    // Reset everything to defaults.
    Object.assign(this, this._defaults);

    if (this._userServiceMap) {
      this.update(this._userServiceMap);
    }

    if (overrides) {
      for (const [k, value] of Object.entries(overrides)) {
        const key = k as keyof SettingsOverrides;
        if (value) {
          this[key] = value;
        }
      }
    }
  }
}

const settings = new Settings();
export default settings;
